import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FaHeart, FaHandHoldingHeart, FaStethoscope, FaGraduationCap, FaAmbulance, FaFlask, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import Base from './Base';

function Donate() {
  const [selectedAmount, setSelectedAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');

  const donationOptions = [
    {
      id: 'general',
      name: 'General Donation',
      icon: <FaHeart />,
      description: 'Support our general healthcare services and patient care programs. Your contribution helps us maintain high-quality medical services for all.',
      amount: 'Any Amount',
      benefits: [
        'Supports patient care programs',
        'Helps maintain medical facilities',
        'Funds essential healthcare services',
        'Makes healthcare accessible to all'
      ]
    },
    {
      id: 'equipment',
      name: 'Medical Equipment',
      icon: <FaStethoscope />,
      description: 'Help us acquire advanced medical equipment for better patient care. Modern equipment enables accurate diagnosis and effective treatment.',
      amount: 'PKR 50,000+',
      benefits: [
        'Advanced diagnostic equipment',
        'Surgical instruments',
        'Patient monitoring systems',
        'Life-saving medical devices'
      ]
    },
    {
      id: 'patient-care',
      name: 'Patient Care Fund',
      icon: <FaHandHoldingHeart />,
      description: 'Contribute to our fund that helps patients in need. This fund provides financial assistance to patients who cannot afford treatment.',
      amount: 'PKR 10,000+',
      benefits: [
        'Financial aid for needy patients',
        'Free treatment for deserving cases',
        'Medication support',
        'Emergency medical assistance'
      ]
    },
    {
      id: 'education',
      name: 'Education & Training',
      icon: <FaGraduationCap />,
      description: 'Support medical education and training programs for our staff. Continuous learning ensures our team provides the best care.',
      amount: 'PKR 25,000+',
      benefits: [
        'Staff training programs',
        'Medical conferences',
        'Skill development workshops',
        'Certification courses'
      ]
    },
    {
      id: 'emergency',
      name: 'Emergency Services',
      icon: <FaAmbulance />,
      description: 'Help us maintain and improve our 24/7 emergency services. Your donation ensures we can respond quickly to medical emergencies.',
      amount: 'PKR 30,000+',
      benefits: [
        '24/7 emergency response',
        'Ambulance services',
        'Emergency medical supplies',
        'Rapid response team support'
      ]
    },
    {
      id: 'research',
      name: 'Research & Development',
      icon: <FaFlask />,
      description: 'Support medical research and development initiatives. Research helps us find better treatments and improve patient outcomes.',
      amount: 'PKR 100,000+',
      benefits: [
        'Medical research projects',
        'Clinical trials',
        'Innovation in treatment',
        'Healthcare advancement'
      ]
    }
  ];

  const quickAmounts = ['5,000', '10,000', '25,000', '50,000', '100,000'];

  return (
    <Base>
      <div className="dark:bg-dark min-h-screen transition-colors duration-200">
        <Helmet><title>Donate | Aman Medical Complex Gilgit</title></Helmet>

        {/* Page Header */}
        <section className="relative py-20 bg-gray-100 dark:bg-gray-800 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1512678080530-7760d81faba6?auto=format&fit=crop&q=80&w=1920"
              alt="Donation"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#002147]/80 dark:bg-gray-900/90"></div>
          </div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Support Our Mission</h1>
            <p className="text-white font-medium uppercase tracking-widest">Home / Donate</p>
          </div>
        </section>

        {/* Donation Options */}
        <section className="py-24 container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-[#002147] dark:text-white mb-4">
              Choose Your Donation Category
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg max-w-3xl mx-auto">
              Your generous donation helps us provide better healthcare services to our community. Every contribution makes a difference.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {donationOptions.map((option) => (
              <div
                key={option.id}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all group"
              >
                <div className="w-16 h-16 bg-[#f0f7ff] text-[#007bff] rounded-full flex items-center justify-center text-2xl mb-4 group-hover:bg-[#007bff] group-hover:text-white transition-all">
                  {option.icon}
                </div>
                <h3 className="text-xl font-black text-[#002147] dark:text-white mb-2">
                  {option.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 leading-relaxed">
                  {option.description}
                </p>
                <div className="mb-4">
                  <span className="bg-[#f0f7ff] text-[#007bff] px-3 py-1 rounded-md text-xs font-bold">
                    {option.amount}
                  </span>
                </div>
                <ul className="space-y-2 mb-6">
                  {option.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                      <FaCheckCircle className="text-[#007bff] mt-0.5 flex-shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 bg-[#007bff] hover:bg-[#0062cc] text-white px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 active:scale-95 w-full justify-center shadow-md"
                >
                  Donate Now <FaArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Donation Form */}
        <section className="py-20 bg-gray-50 dark:bg-gray-800/50">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-black text-[#002147] dark:text-white mb-4">
                  Make a Quick Donation
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Select an amount or enter a custom donation amount
                </p>
              </div>

              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#002147] dark:text-white mb-3">
                    Select Amount (PKR)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    {quickAmounts.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => {
                          setSelectedAmount(amount);
                          setCustomAmount('');
                        }}
                        className={`px-4 py-3 rounded-lg font-bold transition-all ${selectedAmount === amount
                          ? 'bg-[#007bff] text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">OR</span>
                    <input
                      type="number"
                      placeholder="Enter Custom Amount"
                      value={customAmount}
                      onChange={(e) => {
                        setCustomAmount(e.target.value);
                        setSelectedAmount('');
                      }}
                      className="flex-1 px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#007bff]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#002147] dark:text-white mb-2">
                    Donation Category
                  </label>
                  <select className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#007bff]">
                    <option>General Donation</option>
                    <option>Medical Equipment</option>
                    <option>Patient Care Fund</option>
                    <option>Education & Training</option>
                    <option>Emergency Services</option>
                    <option>Research & Development</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#002147] dark:text-white mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#007bff]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#002147] dark:text-white mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#007bff]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#002147] dark:text-white mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#007bff]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#002147] dark:text-white mb-2">
                    Message (Optional)
                  </label>
                  <textarea
                    rows="4"
                    placeholder="Any message or dedication..."
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:border-[#007bff]"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="group w-full bg-[#007bff] hover:bg-[#0062cc] text-white px-5 py-3 rounded-full font-bold text-lg transition-all duration-300 hover:scale-[1.02] active:scale-95 shadow-xl flex items-center justify-center gap-3"
                >
                  Proceed to Donate <FaArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Why Donate Section */}
        <section className="py-24 container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-[#002147] dark:text-white mb-4">
              Why Your Donation Matters
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Save Lives',
                desc: 'Your donation directly contributes to saving lives and improving patient outcomes in our community.'
              },
              {
                title: 'Improve Healthcare',
                desc: 'Help us upgrade facilities and equipment to provide better medical care to everyone.'
              },
              {
                title: 'Support Community',
                desc: 'Your contribution helps us serve the community better, especially those in need.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center p-6">
                <h3 className="text-xl font-black text-[#002147] dark:text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Base>
  );
}

export default Donate;

